import type { IntegrationContent } from '@buildautomaton/types';
import type { IntegrationContentFetchResult } from './cloud/types.js';

export function formatIntegrationContentForMcp(result: IntegrationContentFetchResult): string {
  if (!result.ok) {
    const lines = [`status: ${result.code}`, `message: ${result.message}`];
    if (result.integrationType) lines.push(`integrationType: ${result.integrationType}`);
    if (result.retryable) lines.push('retryable: true');
    lines.push(...formatSessionTicketAssociationLines(result.sessionTicketAssociation));
    return lines.join('\n');
  }

  const lines = [
    formatIntegrationContentBody(result.integrationType, result.content),
    ...formatSessionTicketAssociationLines(result.sessionTicketAssociation),
  ];
  return lines.join('\n');
}

function formatSessionTicketAssociationLines(
  association: IntegrationContentFetchResult['sessionTicketAssociation'],
): string[] {
  if (association.status === 'associated') {
    const title = association.externalTicket.displayTitle?.trim() || association.externalTicket.normalizedUrl;
    return ['', '--- session ticket ---', 'status: associated', `displayTitle: ${title}`];
  }
  if (association.status === 'failed') {
    return ['', '--- session ticket ---', 'status: failed', `reason: ${association.reason}`];
  }
  return ['', '--- session ticket ---', `status: ${association.status}`, `reason: ${association.reason}`];
}

function formatIntegrationContentBody(integrationType: string, content: IntegrationContent): string {
  const lines: string[] = [
    `integrationType: ${integrationType}`,
    `title: ${content.title}`,
    `url: ${content.url}`,
    '',
    '--- body ---',
    content.body,
  ];

  if (content.comments?.length) {
    lines.push('', '--- comments ---');
    for (const comment of content.comments) {
      lines.push(`[${comment.createdAt}] ${comment.author}:`, comment.body, '');
    }
  }

  const threadMessages = (content as { threadMessages?: Array<{ displayName: string; text: string; ts: string }> })
    .threadMessages;
  if (threadMessages?.length) {
    lines.push('', '--- thread messages ---');
    for (const msg of threadMessages) {
      lines.push(`[${msg.ts}] ${msg.displayName}: ${msg.text}`);
    }
  }

  if (content.metadata && Object.keys(content.metadata).length > 0) {
    lines.push('', '--- metadata ---', JSON.stringify(content.metadata, null, 2));
  }

  return lines.join('\n');
}
